import React from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocation } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';


interface MobileOptimizedLayoutProps {
  sidebar: React.ReactNode;
  topBar: React.ReactNode;
  children: React.ReactNode;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

export const MobileOptimizedLayout = ({ 
  sidebar, 
  topBar, 
  children, 
  sidebarCollapsed, 
  setSidebarCollapsed 
}: MobileOptimizedLayoutProps) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  
  // Pages that should use full width layout
  const fullWidthPages = [
    '/admin/room-grid',
    '/admin/bookings', 
    '/admin/bookings-list',
    '/admin/analytics',
    '/admin/dashboard',
    '/admin/rooms',
    '/platform/admin'
  ];
  
  const isFullWidthPage = fullWidthPages.some(page => 
    location.pathname === page || location.pathname.startsWith(page + '/')
  );

  if (isMobile) {
    return (
      <div className="flex h-screen bg-background transition-colors duration-300">
        <Sheet>
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex items-center gap-1 p-2 border-b bg-background/95 backdrop-blur-sm sticky top-0 z-30">
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" aria-label="Открыть меню">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              
              {/* Вся навигация теперь в TopBar */}
              <div className="flex-1">
                {topBar}
              </div>
            </div>
            <main className="flex-1 overflow-y-auto p-4 sm:p-6">
              {children}
            </main>
          </div>
          <SheetContent side="left" className="p-0 w-64 bg-sidebar border-r" aria-describedby="mobile-sidebar-description">
            <div id="mobile-sidebar-description" className="sr-only">
              Навигационное меню администратора
            </div>
            <ScrollArea className="h-full">
              {sidebar}
            </ScrollArea>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background transition-colors duration-300">
      <div className="smooth-transition">
        {sidebar}
      </div>
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b">
          {topBar}
        </div>
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
